// Client Direct Cost Control — for clients like CBRE whose invoice has no
// salary/EOBI/PESSI recovery split (the whole "Invoice Amount" is booked as
// SERVICE_CHARGE revenue by their adapter). Without this, Monthly P&L would
// treat their full billing as margin. Finance enters what was actually paid
// out (salary, EOBI, PESSI, other) per client per month; the total feeds
// pnlCalculator's directCostVariance so Gross Profit = revenue - direct cost,
// separate from ALM's own corporate Operating Expenses (rent, ALM payroll,
// etc. — see operatingExpenseService.js), which is a different thing.
import {
  addDoc, collection, getDocs, query, serverTimestamp, where
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { assertPeriodOpen } from "./periodLockService";

const COSTS = "client_direct_costs";

// CLIENT_ADJUSTMENT is posted automatically when a short receipt is accepted as a
// client adjustment (see receiptAllocationService.js); it can also be entered by hand.
export const DIRECT_COST_CATEGORIES = Object.freeze(["SALARY", "EOBI", "PESSI", "OTHER", "CLIENT_ADJUSTMENT"]);

export async function recordDirectCost({ clientCode, accountingPeriod, category, amount, description, paymentDate, referenceNumber }) {
  const code = String(clientCode || "").trim().toUpperCase();
  if (!code) throw new Error("Select a client.");
  if (!accountingPeriod) throw new Error("Enter the accounting period (YYYY-MM).");
  if (!DIRECT_COST_CATEGORIES.includes(category)) throw new Error("Select a valid cost category.");
  const grossAmount = Math.round(Number(amount || 0));
  if (!(grossAmount > 0)) throw new Error("Enter an amount greater than zero.");

  await assertPeriodOpen(accountingPeriod);
  await addDoc(collection(db, COSTS), {
    clientCode: code,
    accountingPeriod,
    category,
    amount: grossAmount,
    description: String(description || "").trim(),
    paymentDate: paymentDate || accountingPeriod + "-01",
    referenceNumber: String(referenceNumber || "").trim(),
    enteredByUid: auth.currentUser?.uid || null,
    enteredByEmail: auth.currentUser?.email || null,
    enteredAt: serverTimestamp()
  });
}

export async function listDirectCosts(clientCode, accountingPeriod) {
  const q = query(
    collection(db, COSTS),
    where("clientCode", "==", clientCode),
    where("accountingPeriod", "==", accountingPeriod)
  );
  const result = await getDocs(q);
  return result.docs.map((d) => ({ id: d.id, ...d.data() })).filter((entry) => !entry.reversed);
}

export async function getDirectCostSummary(clientCode, accountingPeriod) {
  const entries = await listDirectCosts(clientCode, accountingPeriod);
  const byCategory = {};
  entries.forEach((e) => {
    byCategory[e.category] = Math.round((byCategory[e.category] || 0) + Number(e.amount || 0));
  });
  const total = Math.round(entries.reduce((sum, e) => sum + Number(e.amount || 0), 0));
  return { clientCode, accountingPeriod, entries, byCategory, total };
}

// Company-wide total for a period — every client's direct cost entries
// combined, since Monthly P&L's Gross Profit is one company-wide figure.
export async function getDirectCostVarianceForPeriod(accountingPeriod) {
  const q = query(collection(db, COSTS), where("accountingPeriod", "==", accountingPeriod));
  const result = await getDocs(q);
  const entries = result.docs.map((d) => d.data()).filter((entry) => !entry.reversed);
  const byClient = {};
  entries.forEach((e) => {
    byClient[e.clientCode] = Math.round((byClient[e.clientCode] || 0) + Number(e.amount || 0));
  });
  const total = Math.round(entries.reduce((sum, e) => sum + Number(e.amount || 0), 0));
  return { accountingPeriod, total, byClient };
}
